/**
 * @file useUserFavorites.js
 * @description 我的收藏列表
 * @module features/user/composables/useUserFavorites
 */

import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { convertRouteParams } from '@/shared/utils/idHelper'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { getOssImageUrl } from '@/shared/utils/ossImage'
import { fetchUserCollectPageApi } from '@/features/user/api/userApi'

export function useUserFavorites() {
  const router = useRouter()
  const favorites = ref([])
  const accumulatedFavorites = ref([])
  const currentPage = ref(1)
  const _pageSizePc = 10
  const _pageSizeMobile = 6
  const pageSize = ref(10)
  const totalPages = ref(1)
  const totalItems = ref(0)
  const token = ref('')
  const isMobile = ref(false)
  const loading = ref(false)
  const loadingMore = ref(false)
  const favoritesSentinel = ref(null)
  let infiniteObserver = null

  const displayFavorites = computed(() =>
    isMobile.value ? accumulatedFavorites.value : favorites.value
  )
  const favoriteNoMore = computed(() => {
    if (!isMobile.value || totalItems.value === 0) return true
    return accumulatedFavorites.value.length >= totalItems.value
  })
  const showInfiniteEndTip = computed(() => {
    const ps = pageSize.value
    if (totalItems.value <= ps) return false
    if (loading.value || loadingMore.value) return false
    if (displayFavorites.value.length === 0) return false
    if (isMobile.value) {
      return favoriteNoMore.value
    }
    return currentPage.value >= totalPages.value && totalPages.value >= 1
  })

  function updateIsMobile() {
    isMobile.value = isMobileLayoutViewport()
    pageSize.value = isMobile.value ? _pageSizeMobile : _pageSizePc
  }

  function unbindInfiniteObserver() {
    if (infiniteObserver) {
      infiniteObserver.disconnect()
      infiniteObserver = null
    }
  }

  function bindInfiniteObserver() {
    unbindInfiniteObserver()
    if (!isMobile.value || typeof IntersectionObserver === 'undefined') return
    const el = favoritesSentinel.value
    if (!el || favoriteNoMore.value || loading.value || loadingMore.value) return
    infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          favoriteNoMore.value ||
          loadingMore.value ||
          loading.value ||
          !isMobile.value
        ) {
          return
        }
        fetchFavorites(true)
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )
    infiniteObserver.observe(el)
  }

  function resolveRequestPageNum(append) {
    if (append) return currentPage.value + 1
    if (isMobile.value) return 1
    return currentPage.value
  }

  function mapFavoriteItem(item) {
    return {
      id: item.id,
      image: getOssImageUrl(item.cover, 'thumb'),
      title: item.name,
      description: item.synop,
      themes: (item.comicThemeVos || []).map((theme) => theme.themeName),
    }
  }

  async function fetchFavorites(append) {
    if (append) {
      if (
        !isMobile.value ||
        loadingMore.value ||
        favoriteNoMore.value ||
        accumulatedFavorites.value.length >= totalItems.value
      ) {
        return
      }
    }
    const pageNum = resolveRequestPageNum(append)
    if (!append) {
      loading.value = true
    } else {
      loadingMore.value = true
    }
    try {
      const response = await fetchUserCollectPageApi({
        pageNum,
        pageSize: pageSize.value,
      })

      if (response.data.code === 200 || response.data.code === 0) {
        const { contents, total } = response.data
        const list = Array.isArray(contents) ? contents : []
        const mapped = list.map(mapFavoriteItem)
        totalItems.value = total || 0
        totalPages.value = Math.max(
          1,
          Math.ceil(totalItems.value / pageSize.value)
        )

        if (!append) {
          favorites.value = mapped
          if (isMobile.value) {
            accumulatedFavorites.value = [...mapped]
            currentPage.value = 1
          } else {
            accumulatedFavorites.value = []
          }
        } else if (mapped.length === 0) {
          currentPage.value = pageNum
        } else {
          accumulatedFavorites.value.push(...mapped)
          currentPage.value = pageNum
        }
      } else {
        console.error('获取收藏列表失败:', response.data.msg)
      }
    } catch (error) {
      console.error('请求收藏列表失败:', error)
    } finally {
      loading.value = false
      loadingMore.value = false
      nextTick(() => bindInfiniteObserver())
    }
  }

  function goToComicDetail(comicId) {
    router.push({
      name: 'ComicDetail',
      params: convertRouteParams({ id: comicId }),
    })
  }

  function nextPage() {
    if (isMobile.value) return
    if (currentPage.value < totalPages.value) {
      currentPage.value++
      fetchFavorites(false)
    }
  }

  function previousPage() {
    if (isMobile.value) return
    if (currentPage.value > 1) {
      currentPage.value--
      fetchFavorites(false)
    }
  }

  function onResize() {
    const prev = isMobile.value
    updateIsMobile()
    if (prev !== isMobile.value) {
      currentPage.value = 1
      accumulatedFavorites.value = []
      fetchFavorites(false)
    }
  }

  onMounted(() => {
    token.value = JSON.parse(localStorage.getItem('user'))?.token || ''
    if (!token.value) {
      console.error('未找到用户授权信息，请登录后重试')
    }
    updateIsMobile()
    window.addEventListener('resize', onResize)
    fetchFavorites(false)
    nextTick(() => bindInfiniteObserver())
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
    unbindInfiniteObserver()
  })

  return {
    loading,
    loadingMore,
    isMobile,
    favoritesSentinel,
    displayFavorites,
    showInfiniteEndTip,
    currentPage,
    totalPages,
    totalItems,
    goToComicDetail,
    nextPage,
    previousPage,
  }
}
